// ---------------------------------------------
// SRVListingDetailModal.jsx (modernisierte Version mit Status-Badge + Eigenes-Angebot-Kennzeichnung)
// ---------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../axiosSetup";
import dayjs from "dayjs";
import "../css/marketplace-modal.css";

/* --------------------------------------------
   Fehlermeldungen
--------------------------------------------- */
function extractServerMessage(err) {
  const serverMsg = err?.response?.data?.message;
  if (serverMsg) return serverMsg;

  const status = err?.response?.status;
  switch (status) {
    case 422: return "Eingabe/Regel verletzt.";
    case 409: return "Konflikt: Vorgang aktuell nicht möglich.";
    case 403: return "Keine Berechtigung.";
    case 404: return "Angebot wurde nicht gefunden.";
    case 500: return "Serverfehler. Bitte später erneut versuchen.";
    default:  return err?.message || "Unbekannter Fehler.";
  }
}

/* --------------------------------------------
   Modernes Status-Badge für Listing-Status
   Unterstützt: aktiv | pausiert | abgelaufen | archiviert
--------------------------------------------- */
const LISTING_STATUS_STYLES = {
  aktiv: {
    bg: "linear-gradient(180deg, #dcfce7, #bbf7d0)",
    border: "#22c55e",
    text: "#166534",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    label: "Aktiv",
  },
  pausiert: {
    bg: "linear-gradient(180deg, #e0f2fe, #dbeafe)",
    border: "#60a5fa",
    text: "#1e40af",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M9 7v10M15 7v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    label: "Pausiert",
  },
  abgelaufen: {
    bg: "linear-gradient(180deg, #f3f4f6, #e5e7eb)",
    border: "#9ca3af",
    text: "#374151",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    label: "Abgelaufen",
  },
  archiviert: {
    bg: "linear-gradient(180deg, #ede9fe, #ddd6fe)",
    border: "#8b5cf6",
    text: "#5b21b6",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 7h16M7 7v10a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    label: "Archiviert",
  },
};

function StatusChip({ status }) {
  const key = (status ?? "").toString().toLowerCase().trim();
  const meta =
    LISTING_STATUS_STYLES[key] ||
    { bg: "linear-gradient(180deg, #eef2ff, #e0e7ff)", border: "#a5b4fc", text: "#3730a3", label: status || "Unbekannt", icon: null };

  const style = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background: meta.bg,
    color: meta.text,
    border: `1px solid ${meta.border}`,
    userSelect: "none",
  };

  return (
    <span style={style} title={meta.label}>
      <span style={{ display: "inline-flex", alignItems: "center" }}>{meta.icon}</span>
      <span>{meta.label}</span>
    </span>
  );
}

/* --------------------------------------------
   Kleiner Info-Chip (hell/violett) für "Eigenes Angebot"
--------------------------------------------- */
function OwnListingChip() {
  return (
    <span
      title="Dein eigenes Angebot"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid rgba(139,92,246,.45)",
        background: "rgba(139,92,246,.15)",
        color: "#6d28d9",
        fontWeight: 700,
        fontSize: 12,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 6v12M6 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      Eigenes Angebot
    </span>
  );
}

const SRVListingDetailModal = ({ open, onClose, listing, onOfferCreated }) => {
  const [myAssignments, setMyAssignments] = useState([]);
  const [offerAssignmentId, setOfferAssignmentId] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [banner, setBanner] = useState({ type: null, text: "" });
  const bannerRef = useRef(null);

  const [query, setQuery] = useState("");

  const token = localStorage.getItem("authToken");

  const showBanner = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => {
      bannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      bannerRef.current?.focus?.();
    }, 50);
  };

  /* --------------------------------------------
     Einsätze laden
  --------------------------------------------- */
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get("/api/personell-workassignment-swap", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = Array.isArray(res.data) ? res.data : [];

        // eigenen Einsatz des Listings ausblenden
        const filtered = list.filter((a) => a.assignmentID !== listing.assignmentId);

        setMyAssignments(filtered);
        setBanner({ type: null, text: "" });
        setQuery("");

        if (!filtered.find((a) => String(a.assignmentID) === String(offerAssignmentId))) {
          setOfferAssignmentId("");
        }
      } catch (err) {
        showBanner("error", extractServerMessage(err) || "Fehler beim Laden deiner Einsätze.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token, listing?.assignmentId]);

  /* --------------------------------------------
     Gegenangebot erlaubt?
     (bei eigenen Listings NIE)
  --------------------------------------------- */
  const canOffer = useMemo(() => {
    return (
      listing &&
      listing.status === "aktiv" &&
      (!listing.expiresAt || dayjs(listing.expiresAt).isAfter(dayjs())) &&
      !listing.isOwnListing // ⬅️ NEU: eigene Angebote blocken Offers
    );
  }, [listing]);

  /* --------------------------------------------
     Suchfilter
  --------------------------------------------- */
  const filteredAssignments = useMemo(() => {
    if (!query.trim()) return myAssignments;
    const q = query.trim().toLowerCase();
    return myAssignments.filter((a) => {
      const name = String(a.assignmentName || "").toLowerCase();
      const datesStr = Array.isArray(a.assignment_dates)
        ? a.assignment_dates.map(d => `${d.workDate ?? ""} ${d.timeFrom ?? ""} ${d.timeTo ?? ""}`).join(" ").toLowerCase()
        : String(a.dates || "").toLowerCase();
      return name.includes(q) || datesStr.includes(q);
    });
  }, [myAssignments, query]);

  /* --------------------------------------------
     Angebot senden
  --------------------------------------------- */
  const handleCreateOffer = async () => {
    if (!offerAssignmentId) {
      return showBanner("info", "Bitte wähle einen deiner Einsätze für das Gegenangebot aus.");
    }

    if (!listing?.listingId || listing.listingId <= 0) {
      return showBanner("error", "Angebot-ID fehlt – bitte Fenster schließen und erneut öffnen.");
    }

    setSubmitting(true);
    try {
      const res = await api.post(
        `/api/swap-marketplace/listings/${listing.listingId}/offers`,
        {
          offerAssignmentId: Number(offerAssignmentId),
          message: message?.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showBanner("success", res?.data?.message || "Gegenangebot wurde erstellt.");
      setTimeout(() => onOfferCreated?.(), 400);
    } catch (err) {
      const msg = extractServerMessage(err);
      const code = err?.response?.data?.code;

      const warnCodes = new Set([
        "OFFER_DUPLICATE_ACTIVE_SAME_ASSIGNMENT",
        "OFFER_DUPLICATE_SAME_ASSIGNMENT_PREVIOUS",
      ]);

      const moreWarnCodes = new Set([
        "LISTING_NOT_ACTIVE_OR_EXPIRED",
        "ALREADY_ASSIGNED_TO_TARGET",
        "OFFER_ASSIGNMENT_NOT_OWNED",
        "OWNER_ALREADY_ASSIGNED_TO_OFFER_ASSIGNMENT",
        "OFFER_ASSIGNMENT_EQUALS_TARGET",
        "TARGET_HAS_NO_FUTURE_DATES",
        "OFFER_HAS_NO_FUTURE_DATES",
        "OFFER_ASSIGNMENT_ALREADY_LISTED",
        "OFFER_ASSIGNMENT_ALREADY_IN_ACTIVE_OFFER",
      ]);

      const type = warnCodes.has(code) || moreWarnCodes.has(code) ? "warning" : "error";
      showBanner(type, msg);
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------------------------------
     MODERN: Empty State
  --------------------------------------------- */
  const renderEmptyState = () => (
    <div
      style={{
        padding: "28px 20px",
        marginTop: 10,
        borderRadius: 12,
        background: "#fef3c7",
        border: "1px solid #fcd34d",
        color: "#92400e",
        textAlign: "center",
        fontWeight: 600,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ fontSize: 48, marginBottom: 8, opacity: 0.85 }}>⚠️</div>
      <div style={{ fontSize: 18 }}>Keine tauschbaren Einsätze vorhanden</div>
      <div style={{ fontSize: 14, marginTop: 6 }}>
        Du hast derzeit keine zukünftigen Einsätze, die du für ein Gegenangebot verwenden kannst.
      </div>
    </div>
  );

  /* --------------------------------------------
     MODERN: Assignment Card
  --------------------------------------------- */
  const renderAssignmentCard = (a) => {
    const isSelected = String(offerAssignmentId) === String(a.assignmentID);

    return (
      <button
        key={a.assignmentID}
        type="button"
        onClick={() => setOfferAssignmentId(String(a.assignmentID))}
        className="offer-modern-card"
        disabled={!canOffer || submitting}
        style={{
          width: "100%",
          textAlign: "left",
          background: isSelected ? "#eff6ff" : "white",
          border: isSelected ? "2px solid #2563eb" : "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "14px 16px",
          cursor: canOffer ? "pointer" : "not-allowed",
          transition: "all 0.15s ease",
          boxShadow: isSelected ? "0 6px 14px rgba(37,99,235,0.25)" : "0 2px 6px rgba(0,0,0,0.06)",
        }}
      >
        {/* Titel + Checkmark */}
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ fontWeight: 700, marginRight: 12 }}>{a.assignmentName}</div>
          {isSelected && (
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "#2563eb",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ✓
            </div>
          )}
        </div>

        {/* Termine */}
        <div style={{ marginTop: 8, fontSize: 14, color: "#374151" }}>
          {Array.isArray(a.assignment_dates) && a.assignment_dates.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {a.assignment_dates.slice(0, 3).map((d, i) => (
                <li key={i}>
                  {dayjs(d.workDate).format("DD.MM.YYYY")} — {String(d.timeFrom).slice(0, 5)}–{String(d.timeTo).slice(0, 5)} Uhr
                </li>
              ))}
              {a.assignment_dates.length > 3 && (
                <li style={{ opacity: 0.7 }}>… {a.assignment_dates.length - 3} weitere Termine</li>
              )}
            </ul>
          ) : (
            <div>{a.dates || "Keine Terminangaben verfügbar"}</div>
          )}
        </div>
      </button>
    );
  };

  if (!open) return null;

  /* --------------------------------------------
     MODAL LAYOUT
  --------------------------------------------- */
  const markup = (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 marketplace-modal"
      style={{ background: "rgba(0,0,0,0.6)", zIndex: 1050 }}
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="container h-100 d-flex align-items-center justify-content-center">
        <div
          className="card shadow modal-card-contrast"
          style={{ width: "min(860px, 96vw)", maxHeight: "90vh", overflow: "hidden", borderRadius: 14 }}
        >

          {/* Header */}
          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              padding: "14px 16px",
              background: "linear-gradient(180deg,#f8fafc,#eef2f7)",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <h5 className="m-0">{listing.assignmentName}</h5>
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Schließen</button>
          </div>

          {/* Body */}
          <div style={{ overflowY: "auto" }}>
            <div className="p-3">

              {/* --- Listing Infos oben --- */}
              <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                {/* Status-Badge */}
                <StatusChip status={listing.status} />

                {/* ⬅️ NEU: Eigenes Angebot Chip */}
                {listing.isOwnListing && <OwnListingChip />}
              </div>

              <div className="row g-3 mb-3">
                <Detail label="Anbieter" value={listing.ownerName} />
                <Detail label="Erstellt am" value={dayjs(listing.createdAt).format("DD.MM.YYYY HH:mm")} />
                <Detail label="Ablauf" value={listing.expiresAt ? dayjs(listing.expiresAt).format("DD.MM.YYYY HH:mm") : "—"} />
                <Detail label="Anzahl Gegenangebote" value={listing.offersCount ?? 0} />
              </div>

              {listing.reason && (
                <div className="mt-3">
                  <div className="fw-semibold">Grund</div>
                  <div>{listing.reason}</div>
                </div>
              )}

              {Array.isArray(listing.dates) && listing.dates.length > 0 && (
                <div className="mt-3">
                  <div className="fw-semibold">Einsatztermine</div>
                  <ul className="m-0 ps-3">
                    {listing.dates.map((d, i) => (
                      <li key={i}>
                        {dayjs(d.workDate).format("DD.MM.YYYY")} — {String(d.timeFrom).slice(0, 5)}–{String(d.timeTo).slice(0, 5)} Uhr
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <hr className="my-4" />

              {/* ---------- Gegenangebot erstellen ---------- */}
              <div className="mb-2">
                <h6 className="m-0">Gegenangebot erstellen</h6>

                {/* ⬅️ NEU: Hinweis für eigene Listings */}
                {listing.isOwnListing && (
                  <div
                    className="mt-2"
                    style={{
                      padding: "8px 10px",
                      background: "rgba(139,92,246,.12)",
                      border: "1px solid rgba(139,92,246,.35)",
                      color: "#6d28d9",
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  >
                    Das ist dein eigenes Angebot. Du kannst dafür kein Gegenangebot erstellen.
                  </div>
                )}

                {/* Bestehender Hinweis für andere Sperren (Ablauf/Archiv) */}
                {!canOffer && !listing.isOwnListing && (
                  <div className="text-danger mt-2">
                    Für abgelaufene oder archivierte Angebote können keine Gegenangebote erstellt werden.
                  </div>
                )}
              </div>

              {/* Suchfeld */}
              <div className="row g-2 align-items-end">
                <div className="col-12 col-md-8">
                  <label className="form-label fw-semibold">Meine Einsätze</label>
                  <input
                    className="form-control"
                    placeholder="Nach Name/Datum/Uhrzeit suchen…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    disabled={!canOffer || submitting}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Aktuelles Angebot</label>
                  <input className="form-control" value={listing.assignmentName} disabled readOnly />
                </div>
              </div>

              {/* Karten Grid */}
              <div className="mt-3" style={{ display: "grid", gap: 14 }}>
                {filteredAssignments.length === 0
                  ? renderEmptyState()
                  : filteredAssignments.map((a) => renderAssignmentCard(a))}
              </div>

              {/* Nachricht */}
              <div className="mt-3">
                <label className="form-label fw-semibold">Nachricht (optional)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Kurze Nachricht an den Anbieter…"
                  disabled={!canOffer || submitting}
                />
              </div>

              {/* BUTTONS */}
              <div className="mt-3 d-flex justify-content-end gap-2">
                <button className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
                  Abbrechen
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateOffer}
                  disabled={!canOffer || !offerAssignmentId || submitting}
                >
                  {submitting ? "Sende…" : "Gegenangebot erstellen"}
                </button>
              </div>

            </div>
          </div>

          {/* Bottom Sheet für Banner */}
          {banner.type && (
            <div className={`inline-bottom-sheet inline-bottom-sheet--${banner.type}`}>
              <div className="container px-3 py-2 d-flex align-items-start gap-2">
                <span className="inline-bottom-sheet__dot" />
                <div className="flex-grow-1">{banner.text}</div>
                <button
                  className="btn btn-sm btn-link text-decoration-none"
                  onClick={() => setBanner({ type: null, text: "" })}
                >
                  OK
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );

  const host = document.getElementById("modal-root");
  return host ? createPortal(markup, host) : markup;
};

/* --------------------------------------------
   Detail-Zeile
--------------------------------------------- */
const Detail = ({ label, value }) => (
  <div className="col-12 col-md-6">
    <div className="fw-semibold">{label}</div>
    <div>{value}</div>
  </div>
);

export default SRVListingDetailModal;